/* eslint-disable react/no-array-index-key */
import { LeftOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Checkbox, Drawer, Space, Tag } from 'antd';
import { Confirm, ConfirmReturnRefund } from 'components/Refund';
import { Refuse } from 'components/Refund/Refuse';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { groupBy, isNil, noop, throttle, values } from 'lodash-es';
import {
  HTMLAttributes,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslate } from '@tolgee/react';
import { ReturnRecord } from 'views/Refund/components/ReturnRecord';
import Copy from './Copy';
import { TradeContext } from './StateContext';
import TradeBuyerMemos from './TradeBuyerMemos';
import { useTradeListContext } from './TradeContext';
import TradeDetailLogistics from './TradeDetailLogistics';
import TradeDetailPayIncome from './TradeDetailPayIncome';
import TradeProduct from './TradeProduct';
import TradeReturnProduct from './TradeReturnProduct';
import useInvoice, { StoreContext } from './useInvoice';
import { useConversationReadonlyValue } from 'hooks/business/conversation';
import { isTiKTok } from 'lib/utils/isTikTok';
import { SendLogisticsInfo } from '../components/SendLogisticsInfo';
import { QIFeature } from 'utils/featureFlag';
import { cx } from '@emotion/css';
import SendLogistics from './SendLogistics';
import { TradeInvoice } from './TradeInvoice';
import { getLazadaRefundAmount } from './utils';

const Container = styled.div<{ exportMode?: boolean }>`
  padding: 0px 8px 0 16px;
  background: #fff;
  overflow-y: ${({ exportMode }) => (exportMode ? 'initial' : 'scroll')};
`;

const TopNav = styled.div`
  background: rgb(255, 255, 255);
  padding: 0 0 0 16px !important;
  line-height: 36px;
  width: 100%;
  border-bottom: solid 1px rgba(0, 0, 0, 0.05);
  .trade-nav-active {
    color: #336fff;
    background: #f0f7ff;
  }
`;

const TypeBar = styled.div`
  border-left: 3px solid #3370ff;
  padding-left: 8px;
  font-weight: bold;
  margin: 8px 0 8px -16px !important;
  .ant-tag {
    border: none;
    color: #00bf69;
    margin-left: 8px;
    padding: 0 4px;
    font-size: 12px;
    background: #ebfaf3;
    font-weight: normal;
  }
`;

const BtnJump = styled(Button)`
  background: #f0f2f5;
  border-radius: 2px;
`;

const OrderWrap = styled.div`
  > div {
    display: flex;
    margin-bottom: 8px;
    > div:first-child {
      width: 84px;
      font-size: 14px;
      flex-shrink: 0;
      color: #515359;
    }
    color: #232426;
  }

  .orderBuyerMemos {
    section {
      padding: 0 !important;
      background: none;
      span {
        color: #232426;
      }
    }
  }

  .ant-space-item {
    word-break: break-all;
  }
`;

const MemoDetail = styled.div<{ controlStatus: boolean }>`
  border-bottom: none;
  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 24px;
    font-size: 12px;
    color: #515359;
    div {
      color: ${({ controlStatus }) =>
        controlStatus ? 'var(--fett-app-primary-color)' : '#00000040'};
      cursor: ${({ controlStatus }) => (controlStatus ? 'pointer' : 'not-allowed')};
    }
  }
  > span {
    display: block;
    font-size: 14px;
    color: #232426;
    border-radius: 2px;
    word-break: break-all;
  }
`;

const Header = styled.div`
  position: relative;
  height: 48px;
  line-height: 48px;
  font-weight: 500;
  text-align: center;
  box-shadow: inset 0 -1px 0 0 rgba(225, 230, 238, 1);
`;
const HeaderBack = styled(Button)`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const SpanCOD = styled.span`
  color: #84868c;
  display: flex;
  b {
    font-weight: normal;
    max-width: 120px;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const PromotionType = styled.div`
  padding: 4px 8px;
  background: #f0f7ff;
`;

const PromotionTypeTag = styled(Tag)`
  color: var(--ant-primary-color);
  border-color: var(--ant-primary-color);
`;

const ProductsWrap = styled.div`
  border-bottom: 1px solid rgb(225 230 238);
`;

const RefundOperate = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  > * + * {
    margin-left: 8px;
  }
`;

export const BookingTag = styled.span`
  padding: 1px 2px;
  font-size: 12px;
  color: var(--ant-primary-color);
  background: var(--ant-primary-2);
  border-radius: 2px;
`;

export interface TradeComponentProps extends HTMLAttributes<HTMLDivElement> {
  trade: Conversation.Trade.TradeWithLogistics;
  conversation: Conversation.Conversation;
  width?: number;
  onUpdate: (trade: Conversation.Trade.TradeWithLogistics) => void;
  onDetailClose: () => void;
  exportMode?: boolean; // 这里订单详情页和drawer耦合太严重了，不单独抽离出来了，直接变量控制drawer和其他dom的显示和行为
}

const componentNameMap = {
  /**
   * 订单
   */
  detailTrade: 'detailTrade',
  /**
   * 物流
   */
  detailLogistic: 'detailLogistic',
  /**
   * 退款
   */
  detailRefund: 'detailRefund',
  /**
   * 商品
   */
  detailProduct: 'detailProduct',
  /**
   * 付款
   */
  detailPay: 'detailPay',
} as const;
type ComponentNameType = keyof typeof componentNameMap;

export function TradeComponentDetailComponent(
  props: TradeComponentProps & {
    useInvoiceRes?: ReturnType<typeof useInvoice>;
  },
) {
  const {
    trade,
    exportMode = false,
    conversation,
    useInvoiceRes,
    width,
    onUpdate,
    onDetailClose,
    ...otherProps
  } = props;
  const {
    externalShopId,
    internalBuyerId,
    externalTradeId,
    invoiceNumber,
    items,
    address = '',
    paymentMethod,
    containsPreOrderItem,
    platform,
    unit,
    phone,
    matchBooking,
    bookingId,
    postCode,
    buyerName,
    taxIdType,
    taxIdNumber,
  } = trade;

  const { controlStatus, storeId } = conversation;

  const { t } = useTranslate();

  const displaySensitive = useGetPermission()('订单敏感信息');

  const { refreshTrade } = useTradeListContext();

  const refreshList = useCallback(() => {
    refreshTrade(
      {
        page_no: 1,
        page_size: 1,
        internal_buyer_id: internalBuyerId,
        internal_store_id: storeId,
        external_trade_ids: externalTradeId,
      },
      'LAZADA',
    );
  }, [refreshTrade, internalBuyerId, storeId, externalTradeId]);

  const { commentContent } = useMemo(() => {
    const { tradeStatus: status, sellerMemos } = trade;

    let commentContent = null;

    if (status !== 'CANCEL' || sellerMemos) {
      commentContent = sellerMemos ? (
        <MemoDetail key="commentContent" controlStatus={controlStatus}>
          <div>{t('trade.commentContent')}</div>
          <span>{sellerMemos}</span>
        </MemoDetail>
      ) : null;
    }

    return {
      commentContent,
    };
  }, [trade, t, controlStatus]);

  const commentBuyer = useMemo(() => {
    return <TradeBuyerMemos key="buyerMemos" trade={trade} className="orderBuyerMemos" />;
  }, [trade]);

  const tradeDetailLogisticsUI = <TradeDetailLogistics exportMode={exportMode} trade={trade} />;
  const tradeDetailPayIncomeUI = (
    <TradeDetailPayIncome exportMode={exportMode} trade={trade} className="tradeDetail" />
  );

  const products = useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    const groupItems = groupBy(items, (item) => {
      const { promotionId } = item;
      // 同一个id算一个套装
      return promotionId;
    });

    const productsUi = Object.keys(groupItems).map((promotionId) => {
      const items = groupItems[promotionId];
      const promotionType = items?.[0].promotionType;

      const itemsRender = items.map((item, index) => {
        const { itemId, itemSku: sku, promotionType, promotionRole } = item;
        const namePrefix =
          promotionType === 'add_on_deal' && promotionRole && promotionRole !== 'main' ? (
            <Tag color="gold">{t(`trade.${promotionRole}`)}</Tag>
          ) : null;
        return (
          <TradeProduct
            className="my-1 -mx-1"
            key={`${itemId}-${sku}-${index}`}
            product={item}
            conversation={conversation}
            productIndex={index}
            exportMode={exportMode}
            namePrefix={namePrefix}
            externalShopId={externalShopId}
            showReturn
          />
        );
      });

      return (
        <ProductsWrap>
          {promotionType && (
            <PromotionType>
              <PromotionTypeTag>{t(`trade.${promotionType}`)}</PromotionTypeTag>
            </PromotionType>
          )}
          {itemsRender}
        </ProductsWrap>
      );
    });

    return productsUi;
  }, [items, t, conversation, externalShopId, exportMode]);
  // click 点击滑动  smooth 鼠标或者触控板滑动
  const scrollState = useRef<'click' | 'smooth'>('smooth');
  const [currentNav, setCurrentNav] = useState<ComponentNameType>('detailTrade');
  const uiRoot = useRef<HTMLDivElement>(null);
  const uiTrade = useRef<HTMLDivElement>(null);
  const uiLogistic = useRef<HTMLDivElement>(null);
  const uiRefund = useRef<HTMLDivElement>(null);
  const uiProduct = useRef<HTMLDivElement>(null);
  const uiPay = useRef<HTMLDivElement>(null);
  const uiRefs: Record<ComponentNameType, Element | null> = {
    detailTrade: uiTrade.current,
    detailLogistic: uiLogistic.current,
    detailRefund: uiRefund.current,
    detailProduct: uiProduct.current,
    detailPay: uiPay.current,
  };
  const uiMap = useRef<typeof uiRefs>(uiRefs);
  uiMap.current = uiRefs;
  const { current: mapRefs } = uiMap;
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      throttle<(entries: IntersectionObserverEntry[], observer: IntersectionObserver) => void>(
        (entries) => {
          if (scrollState.current === 'click') {
            return;
          }
          for (const entry of entries) {
            const { isIntersecting, target } = entry;
            if (isIntersecting) {
              const id = target.getAttribute('data-id') as ComponentNameType | null;
              if (id) {
                setCurrentNav(id);
                break;
              }
            }
          }
        },
        200,
      ),
      {
        root: uiRoot.current,
        rootMargin: '-20px 0px 0px 0px',
      },
    );

    values(mapRefs).forEach((v) => {
      if (isNil(v)) {
        return;
      }
      intersectionObserver.observe(v);
    });
    return () => {
      intersectionObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...values(mapRefs)]);

  const [checkedList, setCheckedList] = useState<string[]>([]);

  const { returnList, refundProducts, returnRecords } = useMemo(() => {
    if (items.length === 0) {
      return {
        returnList: [],
        refundProducts: null,
      };
    }

    const returns: Record<string, Conversation.Trade.ReturnProduct[]> = {};
    const returnRecords: Array<{
      returnSn: string;
      data: Conversation.Trade.ReturnHistory[];
    }> = [];

    items.forEach((item, index) => {
      if (item.refundReason?.length) {
        item.refundReason.forEach((refundReason) => {
          const { returnSn, displayStatus, returnHistory } = refundReason;
          if (returnSn && displayStatus !== 'REFUND_COMPLETED') {
            if (Array.isArray(returns[returnSn])) {
              returns[returnSn].push({ ...item, index });
            } else {
              // eslint-disable-next-line no-param-reassign
              returns[returnSn] = [{ ...item, index }];
            }
          }

          returnRecords.push({
            data: returnHistory,
            returnSn,
          });
        });
      }
    });

    if (platform !== 'lazada') {
      const returnList = Object.keys(returns);

      return {
        returnList,
        refundProducts: returnList.map((sn) => (
          <TradeReturnProduct
            exportMode={exportMode}
            sn={sn}
            conversation={conversation}
            data={returns[sn]}
            trade={trade}
          />
        )),
        returnRecords,
      };
    }

    const returnMap = Object.keys(returns).reduce<
      Record<string, Record<string, Conversation.Trade.ReturnProduct>>
    >((res, sn) => {
      const { [sn]: list } = returns;

      res[sn] = list.reduce<Record<string, Conversation.Trade.ReturnProduct>>((res, item) => {
        const { refundReason: [{ returnOrderItemId = undefined } = {}] = [] } = item;

        if (returnOrderItemId) {
          res[returnOrderItemId] = item;
        }

        return res;
      }, {});

      return res;
    }, {});

    const returnList = Object.keys(returnMap).reduce<[string, string][]>((res, sn) => {
      const { [sn]: map } = returnMap;

      res.push(
        ...Object.keys(map).map<[string, string]>((returnOrderItemId) => [sn, returnOrderItemId]),
      );

      return res;
    }, []);

    const canCheck = ([sn, returnOrderItemId]: [string, string]) => {
      const {
        [sn]: { [returnOrderItemId]: { refundReason: [{ operate = [] } = {}] = [] } = {} } = {},
      } = returnMap;

      return operate.filter((_) => _ !== 'STATUS_HISTORY').length !== 0;
    };

    const operateReturnList = returnList.filter(canCheck);

    if (Object.keys(returnMap).length > 1 || operateReturnList.length === 0) {
      return {
        returnList,
        refundProducts: returnList.map(([sn, returnOrderItemId]) => (
          <TradeReturnProduct
            sn={sn}
            conversation={conversation}
            data={[returnMap[sn][returnOrderItemId]]}
            trade={trade}
          />
        )),
      };
    }

    const checked = ([, returnOrderItemId]: [string, string]) => {
      return checkedList.includes(returnOrderItemId);
    };

    const batchOperate = (() => {
      if (operateReturnList.length <= 1) {
        return null;
      }

      const allChecked = operateReturnList.every(checked);

      const indeterminate = !allChecked && operateReturnList.some(checked);

      const enable = (operate: Conversation.Trade.IReturnOperationType) => {
        return (
          checkedList.length > 0 &&
          checkedList.every((id) => {
            return returnList.some(([sn, returnOrderItemId]) => {
              const {
                [sn]: {
                  [returnOrderItemId]: {
                    refundReason: [{ operate: operates = [] } = {}] = [],
                  } = {},
                } = {},
              } = returnMap;

              return id === returnOrderItemId && operates.includes(operate);
            });
          })
        );
      };

      const returnSn = returnList[0][0];

      return (
        <RefundOperate>
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={({ target: { checked } }) =>
              setCheckedList(
                checked ? operateReturnList.map(([, returnOrderItemId]) => returnOrderItemId) : [],
              )
            }
          />
          <Confirm
            externalStoreId={externalShopId}
            externalTradeId={externalTradeId}
            returnSn={returnSn}
            onSuccess={refreshList}
            operate="CONFIRM"
            platform="LAZADA"
            returnOrderItemId={checkedList}
            disabled={!enable('CONFIRM')}
            unit={unit}
            refundAmount={getLazadaRefundAmount(checkedList, items)}
          />
          <ConfirmReturnRefund
            externalStoreId={externalShopId}
            externalTradeId={externalTradeId}
            returnSn={returnSn}
            platform="LAZADA"
            returnOrderItemId={checkedList}
            disabled={!enable('CONFIRM_RETURN_REFUND')}
            onSucceed={refreshList}
          />
          <Refuse
            internalStoreId={storeId}
            externalStoreId={externalShopId}
            returnSn={returnSn}
            returnOrderItemId={checkedList}
            onRefused={refreshList}
            disabled={!enable('REFUSE')}
          />
        </RefundOperate>
      );
    })();

    const refundProducts = (
      <>
        {batchOperate}
        {returnList.map(([sn, returnOrderItemId]) => {
          return (
            <TradeReturnProduct
              sn={sn}
              conversation={conversation}
              data={[returnMap[sn][returnOrderItemId]]}
              trade={trade}
              checked={
                operateReturnList.length > 1 && canCheck([sn, returnOrderItemId])
                  ? checked([sn, returnOrderItemId])
                  : undefined
              }
              onCheckedChange={(checked) =>
                setCheckedList(
                  checked
                    ? [...checkedList, returnOrderItemId].filter((_) => _)
                    : checkedList.filter((_) => _ !== returnOrderItemId),
                )
              }
            />
          );
        })}
      </>
    );

    return {
      returnList,
      refundProducts,
    };
  }, [
    conversation,
    items,
    trade,
    platform,
    checkedList,
    externalShopId,
    storeId,
    externalTradeId,
    unit,
    refreshList,
    exportMode,
  ]);

  const hasRefund = useMemo(() => returnList && returnList.length > 0, [returnList]);

  const scrollIntoTargetNav = (id: ComponentNameType) => {
    setCurrentNav(id);
    scrollState.current = 'click';
    setTimeout(() => {
      scrollState.current = 'smooth';
    }, 200);
    mapRefs[id]?.scrollIntoView();
  };
  const activeNav = 'trade-nav-active';

  const displayTradeId = platform === 'tokopedia' ? invoiceNumber : externalTradeId;

  const sendLogistics = useMemo(() => {
    if (exportMode) return null;
    if (platform === 'lazada' && trade.lazadaLogistics?.packages) {
      const screenshotPackages = trade.lazadaLogistics.packages.map((logisticsPackage) => ({
        trackingNumber: logisticsPackage.trackingNumber,
        shippingCarrier: logisticsPackage.shippingCarrier,
        trackingInfo: logisticsPackage.trackingInfo.map((tracking) => ({
          updateTime: tracking.updateTime,
          description: tracking.description,
        })),
      }));
      return <SendLogisticsInfo logisticsPackages={screenshotPackages} />;
    }
    if (!trade.logisticsList) {
      return null;
    }
    if (isTiKTok(platform)) {
      return <SendLogistics conversation={conversation} logisticsList={trade.logisticsList} />;
    }

    const screenshotPackages = trade.logisticsList.map((logistics) => ({
      trackingNumber: logistics.trackingNumber,
      shippingCarrier: logistics.shippingCarrier,
      firstMileTrackingNumber: logistics.firstMileTrackingNumber,
      trackingInfo: logistics.trackingInfo.map((tracking) => ({
        updateTime: tracking.updateTime,
        description: tracking.description,
      })),
    }));
    return <SendLogisticsInfo logisticsPackages={screenshotPackages} />;
  }, [platform, trade, exportMode, conversation]);

  // 是否显示付款信息
  const showDetailPay = displaySensitive;

  return (
    <>
      <>
        <TopNav>
          <Space size={16}>
            <BtnJump
              type="text"
              size="small"
              onClick={() => scrollIntoTargetNav('detailTrade')}
              className={currentNav === 'detailTrade' ? activeNav : ''}
            >
              {t('tab.trade')}
            </BtnJump>
            <BtnJump
              type="text"
              size="small"
              onClick={() => scrollIntoTargetNav('detailLogistic')}
              className={currentNav === 'detailLogistic' ? activeNav : ''}
            >
              {t('trade.物流')}
            </BtnJump>
            {hasRefund && (
              <BtnJump
                type="text"
                size="small"
                onClick={() => scrollIntoTargetNav('detailRefund')}
                className={currentNav === 'detailRefund' ? activeNav : ''}
              >
                {t('trade.退货退款')}
              </BtnJump>
            )}
            <BtnJump
              type="text"
              size="small"
              onClick={() => scrollIntoTargetNav('detailProduct')}
              className={currentNav === 'detailProduct' ? activeNav : ''}
            >
              {t('tab.product')}
            </BtnJump>
            {showDetailPay && (
              <BtnJump
                type="text"
                size="small"
                onClick={() => scrollIntoTargetNav('detailPay')}
                className={`${currentNav === 'detailPay' ? activeNav : ''}`}
              >
                {t('trade.付款')}
              </BtnJump>
            )}
          </Space>
        </TopNav>

        <Container {...otherProps} ref={uiRoot} id="trade-container" exportMode={exportMode}>
          <OrderWrap>
            <TypeBar ref={uiTrade} data-id={componentNameMap.detailTrade}>
              {t('tab.trade')}
              {containsPreOrderItem ? (
                <Tag color="green" title={t('trade.预购')}>
                  {t('trade.预购')}
                </Tag>
              ) : null}
            </TypeBar>
            <div>
              <div>{t('trade.订单编号')}</div>
              <Space>
                {displayTradeId}
                {paymentMethod ? (
                  <SpanCOD title={paymentMethod}>
                    [<b>{paymentMethod}</b>]
                  </SpanCOD>
                ) : null}
                <Copy
                  className={cx({
                    hidden: exportMode,
                  })}
                  text={displayTradeId}
                  successMessage={t('trade.copySucceed')}
                  failedMessage={t('trade.copyFailed')}
                />
              </Space>
            </div>
            {taxIdNumber && (
              <div>
                <div>{taxIdType}</div>
                <Space>
                  {taxIdNumber}
                  <Copy
                    text={taxIdNumber}
                    successMessage={t('trade.复制成功')}
                    failedMessage={t('trade.复制失败')}
                  />
                </Space>
              </div>
            )}
            {buyerName && platform === 'lazada' && (
              <div>
                <div>{t('enterprise.姓名')}</div>
                <Space>{buyerName}</Space>
              </div>
            )}
            {matchBooking && (
              <div>
                <div>BookingID</div>
                <Space>
                  {bookingId
                    ? [
                        bookingId,
                        <Copy
                          text={bookingId}
                          successMessage={t('trade.复制成功')}
                          failedMessage={t('trade.复制失败')}
                        />,
                      ]
                    : '-'}
                  <BookingTag>{t('匹配订单')}</BookingTag>
                </Space>
              </div>
            )}
            {phone && displaySensitive && platform !== 'mercado' ? (
              <div>
                <div>{t('trade.电话号码')}</div>
                <Space>{phone}</Space>
              </div>
            ) : null}
            {address && displaySensitive ? (
              <div>
                <div>{t('trade.收货地址')}</div>
                <Space>
                  <div className="whitespace-pre-wrap">{address}</div>
                  <Copy
                    className={cx({
                      hidden: exportMode,
                    })}
                    text={address}
                    successMessage={t('trade.copySucceed')}
                    failedMessage={t('trade.copyFailed')}
                  />
                </Space>
              </div>
            ) : null}
            {postCode && platform === 'lazada' && (
              <div>
                <div>{t('邮编')}</div>
                <Space>{postCode}</Space>
              </div>
            )}
            {commentBuyer}
            {commentContent}
          </OrderWrap>

          <TradeContext.Provider
            value={{
              state: trade,
              setState: noop,
            }}
          >
            <div className="flex flex-col">
              <TypeBar
                className="flex items-center justify-between"
                ref={uiLogistic}
                data-id={componentNameMap.detailLogistic}
              >
                {t('trade.物流')}
                {sendLogistics}
              </TypeBar>
              {tradeDetailLogisticsUI}
            </div>
            {hasRefund && (
              <div>
                <TypeBar ref={uiRefund} data-id={componentNameMap.detailRefund}>
                  {t('trade.退货退款')}
                </TypeBar>
                {isTiKTok(platform) &&
                  returnRecords?.map(({ returnSn, data }) => (
                    <ReturnRecord type="mini" data={data} returnSn={returnSn} />
                  ))}
                {refundProducts}
              </div>
            )}
            <div>
              <TypeBar ref={uiProduct} data-id={componentNameMap.detailProduct}>
                {t('tab.product')}
              </TypeBar>
              {products}
            </div>
            {showDetailPay && (
              <div>
                <TypeBar ref={uiPay} data-id={componentNameMap.detailPay}>
                  {t('trade.付款')}
                </TypeBar>
                {tradeDetailPayIncomeUI}
              </div>
            )}
          </TradeContext.Provider>
        </Container>
      </>
    </>
  );
}

function TradeComponentDetail(props: TradeComponentProps) {
  const { conversation, trade, exportMode = false, width = 420, onDetailClose } = props;

  const { platform, region } = trade;
  const { t } = useTranslate();

  const { storeId } = useContext(StoreContext);

  const readonly = useConversationReadonlyValue();

  const showFooter = useMemo(() => {
    if (platform === 'mercado' && (conversation.reputation || conversation.mediation)) {
      return false;
    }
    return (
      !QIFeature &&
      !readonly &&
      ['tiktok', 'tiktok_us', 'lazada', 'shopee', 'mercado'].includes(platform)
    );
  }, [readonly, platform, conversation]);

  return (
    <>
      <Drawer
        visible={!exportMode}
        mask={false}
        getContainer={false}
        closable={false}
        bodyStyle={{
          position: 'relative',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        style={{
          width,
          position: 'absolute',
          zIndex: exportMode ? -1 : 30,
        }}
        footer={
          showFooter ? (
            <div className="flex justify-end">
              <TradeInvoice
                externalTradeId={trade.externalTradeId}
                internalBuyerId={trade.internalBuyerId}
                internalStoreId={storeId}
                platform={platform}
                region={region}
                buyerNick={conversation.buyerNick}
              />
            </div>
          ) : null
        }
      >
        <Header>
          <HeaderBack type="link" icon={<LeftOutlined />} onClick={() => onDetailClose()}>
            {t('voucher.back')}
          </HeaderBack>
          {t('trade.订单详情')}
        </Header>
        <TradeComponentDetailComponent {...props} />
      </Drawer>
    </>
  );
}

export default memo(TradeComponentDetail);
